import { Box, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { ContainerGrid, Link, ResponsiveImage, type FC, type ResponsiveImageType } from ".."
import type { LinkType } from "../../graphql/types"
import { find } from "lodash"

type ContentHighlightProps = {
  id?: string
  color?: "blue" | "yellow"
  subtitle: string
  title: string
  description: string
  image: ResponsiveImageType
  link?: LinkType
}

const ContentHighlight: FC<ContentHighlightProps> = ({
  id,
  color = "blue",
  subtitle,
  title,
  description,
  image,
  link,
}) => {
  const isVisible = title && description
  const isImageAvailable = image && image?.variants?.length > 0
  // Medium screen sizes are pixelated due to using sm breakpoint source, so we
  // need to create a fake variant from 3xl as md.
  const imageVariants = image?.variants ?? []
  const imageVariantFake = find(imageVariants, { style: "3xl" })
  const finalImageVariants = imageVariantFake
    ? [...imageVariants, { ...imageVariantFake, style: "md" }]
    : imageVariants
  const augmentedImage = { variants: finalImageVariants || [] }

  if (isVisible) {
    return (
      <ContainerGrid id={id} py={{ xs: 5, md: 10 }} overflow={{ md: "hidden" }}>
        <ContentHighlightContent
          color={color}
          subtitle={subtitle}
          title={title}
          description={description}
          link={link}
        />
        {isImageAvailable ? <ContentHighlightImage image={augmentedImage} /> : <PlaceholderImage />}
      </ContainerGrid>
    )
  }

  return null
}
export default ContentHighlight

/** A sub-component for the content section of the content highlight component */
type ContentProps = Omit<ContentHighlightProps, "image">
const ContentHighlightContent: FC<ContentProps> = ({
  color,
  subtitle,
  title,
  description,
  link,
}) => {
  return (
    <Box
      gridColumn={{ xs: "1 / -1", md: "auto / span 4", lg: "auto / span 6" }}
      order={{ xs: "2", md: "1" }}
      color={(theme) => theme.palette[color === "blue" ? "secondary.800" : "neutral.700"].main}
      bgcolor={(theme) => theme.palette[color === "blue" ? "secondary.200" : "tertiary.300"].main}
      borderRadius={1.5}
      sx={{
        "float": "left",
        "height": "100%",

        "&::before": {
          content: '""',
          display: "block",
          paddingTop: { md: "100%" },
          float: "left",
        },
      }}
    >
      <Stack height="100%">
        <Box
          borderBottom={(theme) =>
            `1px solid ${
              color === "blue"
                ? alpha(theme.palette["secondary.800"].main, 0.2)
                : alpha(theme.palette["neutral.700"].main, 0.1)
            }`
          }
          px={{ xs: 5, md: 10 }}
          py={{ xs: 4, md: 7 }}
        >
          <Typography variant="body.lg" component="h3">{subtitle}</Typography>
        </Box>
        <Box mt={{ xs: 15, md: "auto" }} px={{ xs: 5, md: 10 }} py={{ xs: 8, md: 15 }}>
          <Typography variant="heading.2xl" component="h2" mb={{ xs: 8, md: 10 }}>
            {title}
          </Typography>

          <Box>
            <Typography variant="body.md">
              <Box dangerouslySetInnerHTML={{ __html: description }} />
            </Typography>
          </Box>
          {link?.title && (
            <Box mt={10}>
              <Link
                variant="link.md"
                type="base"
                href={link?.uri}
                sx={(theme) => ({
                  "bgcolor": theme.palette["neutral.100"].main,
                  "boxShadow": `1px 1px 2px ${alpha(theme.palette["secondary.800"].main, 0.15)}`,
                  "border": "1px solid transparent",
                  "borderRadius": 1,
                  "display": "inline-flex",
                  "alignItems": "center",
                  "justifyContent": "center",

                  "color": theme.palette["secondary.800"].main,
                  "lineHeight": 1,
                  "minHeight": { xs: 54, md: 62 },
                  "width": { xs: "100%", md: "auto" },
                  "px": { xs: 7.5, md: 10 },
                  "py": 3.5,
                  "transition": theme.transitions.create([
                    "background-color",
                    "border-color",
                    "box-shadow",
                  ]),

                  "&:hover, &:focus-visible": {
                    bgcolor: theme.palette["secondary.300"].main,
                    boxShadow: `1px 1px 2px ${alpha(theme.palette["secondary.800"].main, 0.4)}`,
                  },
                })}
              >
                {link.title}
              </Link>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  )
}

/** A sub-component for the image section of the content highlight component */
type ImageProps = Pick<ContentHighlightProps, "image">
const ContentHighlightImage: FC<ImageProps> = ({ image }) => {
  return (
    <Box
      borderRadius={1.5}
      overflow="hidden"
      order={{ xs: "1", md: "2" }}
      gridColumn={{ xs: "1 / -1", md: "auto / span 4", lg: "auto / span 6" }}
      height="100%"
      sx={{
        "float": "left",
        "height": "100%",

        "&::before": {
          content: '""',
          display: "block",
          paddingTop: "100%",
          float: "left",
        },
      }}
    >
      <ResponsiveImage image={image} />
    </Box>
  )
}

const PlaceholderImage = () => {
  return (
    <Box
      borderRadius={1.5}
      overflow="hidden"
      order={{ xs: "1", md: "2" }}
      bgcolor={(theme) => alpha(theme.palette["secondary.900"].main, 0.15)}
      gridColumn={{ xs: "1 / -1", md: "auto / span 4", lg: "auto / span 6" }}
      height="100%"
      sx={{
        "float": "left",
        "height": "100%",

        "&::before": {
          content: '""',
          display: "block",
          paddingTop: "100%",
          float: "left",
        },
      }}
    />
  )
}
